import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import { AddAttributesDirective } from '../bar/directives/add-attributes/add-attributes.directive';
import { SideMenuBreadcrumbComponent } from './components/side-menu-breadcrumb/side-menu-breadcrumb.component';
import { SideMenuCallToActionComponent } from './components/side-menu-call-to-action/side-menu-call-to-action.component';
import { SideMenuChildComponent } from './components/side-menu-child/side-menu-child.component';
import { SideMenuParentComponent } from './components/side-menu-parent/side-menu-parent.component';
import { Menu, MenuConfig } from './models/menu.type';
import { IsGrandChildPipe } from './pipes/is-grand-child.pipe';
import { findItemById } from './utils/side-menu.utils';

@Component({
    selector: 'nw-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SideMenuBreadcrumbComponent,
        SideMenuParentComponent,
        SideMenuChildComponent,
        SideMenuCallToActionComponent,
        IsGrandChildPipe,
        CommonModule,
        AddAttributesDirective
    ]
})
export class SideMenuComponent implements OnChanges {
    @Input() menuConfig!: MenuConfig;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onClick: EventEmitter<Menu.ItemConfig> =
        new EventEmitter<Menu.ItemConfig>();
    public cdr = inject(ChangeDetectorRef);

    public menu!: Menu;

    public ngOnChanges({ menuConfig }: SimpleChanges): void {
        if (menuConfig?.currentValue) {
            this.buildMenu(this.menuConfig);
        }
    }

    private buildMenu(menuConfig: MenuConfig): void {
        this.validateConfig();

        this.menu = {
            ...menuConfig,
            // sort menu config so that CTA's are at the bottom
            items: menuConfig.items.sort((a: Menu.ItemConfig, b: Menu.ItemConfig) => {
                if (a?.isCta === b?.isCta) return 0;
                if (a?.isCta) return 1;
                if (b?.isCta) return -1;

                return 0;
            })
        };

        let index: number = 100;
        function addParentReference(
            items: Menu.Item[],
            parentId: number | undefined = undefined
        ): void {
            for (const ITEM of items) {
                ITEM.id = index++;
                ITEM.parentId = parentId;
                ITEM.open = ITEM.active || false;
                // if the item is not a root item, clear isCta
                if (ITEM.parentId && ITEM.isCta) ITEM.isCta = false;

                if (ITEM.items) addParentReference(ITEM.items, ITEM.id);
            }
        }

        addParentReference(this.menu.items);
    }

    private validateConfig(): void {
        // check for mulitple CTA's in the root
        const NUMBER_OF_ROOT_LEVEL_CTAS: number = this.menuConfig.items.filter(
            ({ isCta }) => isCta
        ).length;

        if (NUMBER_OF_ROOT_LEVEL_CTAS > 1)
            throw new Error('NW-SIDE-MENU cannot have more than one call to action');

        // check for nested level don't exceed three
        function getMaxDepth(items?: Menu.Item[], currentDepth: number = 0): number {
            // Initialize the maximum depth to the current depth
            let maxDepth = currentDepth;

            // Check if the item has a nested items array
            if (items && Array.isArray(items)) {
                // Iterate through the nested items
                for (const item of items) {
                    // Recursively get the depth of the nested item
                    const itemDepth = getMaxDepth(item?.items, currentDepth + 1);
                    // Update the maximum depth if the item depth is greater
                    if (itemDepth > maxDepth) {
                        maxDepth = itemDepth;
                    }
                }
            }

            // Return the maximum depth found
            return maxDepth;
        }

        const LEVELS_OF_RECURSION: number = getMaxDepth(this.menuConfig.items);
        if (LEVELS_OF_RECURSION > 3)
            throw new Error('NW-SIDE-MENU cannot have more three levels of recursion');
    }

    public clickParent($event: Event, item: Menu.Item): void {
        if (item.items?.length) {
            item.open = !item.open;
            this.cdr.markForCheck();

            $event.stopPropagation();
            $event.stopImmediatePropagation();

            return;
        }
        this.emit($event, item);
    }

    public emit($event: Event, item: Menu.Item): void {
        if (!item.onClick) {
            if (this.menuConfig.multi === false) {
                resetOpen(this.menu.items, true);
            }
            return;
        }

        const OPEN_STATE: boolean = !!item.open;

        if (this.menuConfig.multi !== false) item.open = !item.open;

        // A link leaf with a callback is the host's to navigate, as on the desktop bar.
        if (item.path) $event.preventDefault();

        if (item.onClick && typeof item.onClick === 'function') item.onClick();

        function resetOpen(items: Menu.Item[], multi: boolean = false): void {
            for (const ITEM of items) {
                if (multi) ITEM.open = false;

                if (ITEM.items) resetOpen(ITEM.items, multi);
            }
        }
        function resetOpenAndActive(items: Menu.Item[], multi: boolean = false): void {
            for (const ITEM of items) {
                ITEM.active = false;
                if (multi) ITEM.open = false;

                if (ITEM.items) resetOpenAndActive(ITEM.items, multi);
            }
        }
        function setOpenAndActive(
            items: Menu.Item[],
            _item: Menu.Item,
            multi: boolean = false
        ): void {
            _item.active = true;

            if (multi && !!_item.items?.length) {
                _item.open = item.id === _item.id ? !OPEN_STATE : !_item.open;
            }

            if (_item.parentId) {
                const ITEM = findItemById(items, _item.parentId);
                if (ITEM) setOpenAndActive(items, ITEM, multi);
            }
        }

        resetOpenAndActive(this.menu.items, this.menuConfig.multi === false);
        setOpenAndActive(this.menu.items, item, this.menuConfig.multi === false);

        this.onClick.emit({
            label: item.label,
            onClick: item?.onClick,
            active: item?.active,
            items: item?.items
        });
    }
}
